import { jsx as _jsx } from "react/jsx-runtime";
import React, { useCallback, useEffect, useReducer, useState } from 'react';
import { TagArticlesService } from '../../services/tagArticlesService';
function reducer(state, action) {
    switch (action.type) {
        case 'FETCH_START':
            return Object.assign(Object.assign({}, state), { isLoading: true, error: null });
        case 'FETCH_SUCCESS_INITIAL':
            return Object.assign(Object.assign({}, state), { isLoading: false, items: action.items, currentPage: action.page, hasNext: action.hasNext });
        case 'FETCH_SUCCESS_APPEND':
            return Object.assign(Object.assign({}, state), { isLoading: false, items: [...state.items, ...action.items], currentPage: action.page, hasNext: action.hasNext });
        case 'FETCH_ERROR':
            return Object.assign(Object.assign({}, state), { isLoading: false, error: action.error });
        default:
            return state;
    }
}
// ─── URL helpers ──────────────────────────────────────────────────────────────
function getPageFromUrl() {
    var _a;
    const params = new URLSearchParams(window.location.search);
    const p = parseInt((_a = params.get('page')) !== null && _a !== void 0 ? _a : '1', 10);
    return Number.isFinite(p) && p >= 1 ? p : 1;
}
function pushPageToUrl(page) {
    const url = new URL(window.location.href);
    url.searchParams.set('page', String(page));
    window.history.pushState({ page }, '', url.toString());
}
const service = new TagArticlesService();
/**
 * Загружает view contract из @hooks/tagArticlesView.
 * Webpack разрешает алиас к проектному хуку или к core-дефолту.
 */
async function loadViewContract() {
    const module = await import('@hooks/tagArticlesView');
    return module.default();
}
/**
 * Загружает tags contract из @hooks/relatedTagsView.
 * Webpack разрешает алиас к проектному хуку или к core-дефолту.
 */
async function loadTagsContract() {
    try {
        const module = await import('@hooks/relatedTagsView');
        return module.default();
    }
    catch (err) {
        console.warn('[TagArticlesIsland] Failed to load tags contract:', err);
        return null;
    }
}
/**
 * Главный компонент острова tag-articles.
 *
 * Поведение (зеркалирует CategoryArticlesIsland):
 * - При монтировании загружает только страницу из URL (или initialPage).
 * - «Load more» загружает следующую страницу, дописывает к списку, обновляет URL.
 * - popstate синхронизирует список с URL (заменяет всё содержимое).
 * - Карточки статей рендерятся через переиспользуемый ArticleCardBS5.
 * - Теги на карточках рендерятся через RelatedTagsInlineBlock (если tagsContract загружен).
 */
export const TagArticlesIsland = ({ bootstrap }) => {
    const { tagId, initialPage, pageSize } = bootstrap;
    const [viewContract, setViewContract] = useState(null);
    const [tagsContract, setTagsContract] = useState(null);
    const [state, dispatch] = useReducer(reducer, {
        items: [],
        currentPage: initialPage,
        hasNext: false,
        isLoading: false,
        error: null,
    });
    // ── Load view contract and tags contract ────────────────────────────────────
    useEffect(() => {
        loadViewContract().then(setViewContract).catch((err) => {
            console.error('[TagArticlesIsland] Failed to load view contract:', err);
        });
        loadTagsContract().then(setTagsContract).catch(() => {
            // Already logged in loadTagsContract
        });
    }, []);
    // ── Initial load ──────────────────────────────────────────────────────────
    const loadPage = useCallback(async (page, mode) => {
        dispatch({ type: 'FETCH_START' });
        try {
            const result = await service.getPage(tagId, { page, size: pageSize });
            if (mode === 'append') {
                dispatch({ type: 'FETCH_SUCCESS_APPEND', items: result.items, page, hasNext: result.hasNext });
            }
            else {
                dispatch({ type: 'FETCH_SUCCESS_INITIAL', items: result.items, page, hasNext: result.hasNext });
            }
        }
        catch (e) {
            const msg = e instanceof Error ? e.message : 'Failed to load articles';
            dispatch({ type: 'FETCH_ERROR', error: msg });
        }
    }, [tagId, pageSize]);
    useEffect(() => {
        const page = getPageFromUrl();
        loadPage(page, 'initial');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    // ── Browser back/forward ──────────────────────────────────────────────────
    useEffect(() => {
        const onPopState = () => {
            const page = getPageFromUrl();
            loadPage(page, 'initial');
        };
        window.addEventListener('popstate', onPopState);
        return () => window.removeEventListener('popstate', onPopState);
    }, [loadPage]);
    // ── Load more ─────────────────────────────────────────────────────────────
    const handleLoadMore = useCallback(() => {
        const nextPage = state.currentPage + 1;
        pushPageToUrl(nextPage);
        loadPage(nextPage, 'append');
    }, [state.currentPage, loadPage]);
    // ── Render ────────────────────────────────────────────────────────────────
    if (!viewContract) {
        return null;
    }
    const { ListComponent, CardComponent, messages } = viewContract;
    return (_jsx(ListComponent, { items: state.items, isLoading: state.isLoading, error: state.error, hasNext: state.hasNext, onLoadMore: handleLoadMore, CardComponent: CardComponent, messages: messages, tagsContract: tagsContract }));
};
